<template>
  <div class="landing">
    <header class="landing-nav">
      <div class="landing-nav__inner">
        <a
          href="#top"
          class="landing-nav__brand"
          aria-label="AGVA home"
        >
          <img
            class="landing-nav__logo"
            :src="companyLogoImage"
            alt="AGVA"
          >
        </a>

        <nav
          class="landing-nav__menu"
          aria-label="Account"
        >
          <template v-if="isAuthReady && !isAuthenticated">
            <router-link
              to="/register"
              class="landing-nav__register"
            >
              Register
            </router-link>
            <router-link
              to="/login"
              class="landing-nav__login"
            >
              Login
            </router-link>
          </template>

          <div
            v-else-if="isAuthReady && isAuthenticated"
            class="landing-nav__authenticated"
          >
            <router-link
              to="/admin"
              class="landing-nav__admin"
            >
              Admin panel
            </router-link>
            <b-navbar-nav class="landing-nav__user">
              <b-nav-item-dropdown
                right
                toggle-class="d-flex align-items-center dropdown-user-link landing-nav__user-toggle"
                class="dropdown-user"
              >
                <template #button-content>
                  <div class="d-flex user-nav navbar-user-nav">
                    <b-avatar
                      :src="userPicture"
                      size="40"
                      variant="light-primary"
                      class="navbar-user-nav__avatar flex-shrink-0"
                    />
                    <div class="navbar-user-nav__info">
                      <p class="user-name font-weight-bolder mb-0">
                        {{ userDisplayName }}
                      </p>
                      <span class="user-status">{{ userDisplayRole }}</span>
                    </div>
                  </div>
                </template>

                <b-dropdown-item
                  link-class="d-flex align-items-center"
                  to="/profile"
                >
                  <feather-icon
                    size="16"
                    icon="UserIcon"
                    class="mr-50"
                  />
                  <span>Profile</span>
                </b-dropdown-item>

                <b-dropdown-divider />

                <b-dropdown-item
                  link-class="d-flex align-items-center"
                  @click="userLogout"
                >
                  <feather-icon
                    size="16"
                    icon="LogOutIcon"
                    class="mr-50"
                  />
                  <span>Logout</span>
                </b-dropdown-item>
              </b-nav-item-dropdown>
            </b-navbar-nav>
          </div>
        </nav>
      </div>
    </header>

    <main id="top">
      <section
        class="landing-hero"
        aria-roledescription="carousel"
        aria-label="Product highlights"
      >
        <div
          v-for="(slide, index) in slides"
          :key="slide.title"
          class="landing-hero__slide"
          :class="{
            'landing-hero__slide--active': index === activeSlide,
            'landing-hero__slide--loaded': Boolean(slide.loadedImage),
          }"
          :style="heroSlideStyle(slide)"
          :aria-hidden="index === activeSlide ? 'false' : 'true'"
        >
          <div
            class="landing-hero__caption"
            :class="[
              `landing-hero__caption--${slide.position || 'top-right'}`,
              `landing-hero__caption--align-${slide.textAlign || 'right'}`,
            ]"
          >
            <h1
              class="landing-hero__title"
              :style="{ color: slide.fontColor || '#000000' }"
            >
              <span
                v-for="(line, lineIndex) in heroTitleLines(slide.title)"
                :key="`${slide.title}-${lineIndex}`"
                class="landing-hero__line"
                :style="{ backgroundColor: slide.backgroundColor || '#ffffff' }"
              >{{ line }}</span>
            </h1>
          </div>
        </div>

        <div class="landing-hero__dots">
          <button
            v-for="(slide, index) in slides"
            :key="slide.title"
            type="button"
            class="landing-hero__dot"
            :class="{ 'is-active': index === activeSlide }"
            :aria-label="slide.title"
            :aria-current="index === activeSlide ? 'true' : 'false'"
            @click="goToSlide(index)"
          />
        </div>
      </section>

      <div
        v-if="isAuthReady && !isAuthenticated"
        class="landing-hero-cta"
      >
        <h1 class="undr-landing-section__title">
          Start with one simple step, no credit card required
        </h1>
        <router-link
          to="/register"
          class="landing-btn landing-btn--primary landing-btn--lg"
        >
          Register your company now
        </router-link>
      </div>

      <section
        id="features"
        class="landing-section"
      >
        <h2 class="landing-section__title">Everything a growing team needs</h2>
        <p class="landing-section__lead">
        </p>
        <ul class="landing-features">
          <li
            v-for="feature in features"
            :key="feature.title"
            class="landing-features__item"
          >
            <span class="landing-features__icon" aria-hidden="true">
              <feather-icon
                :icon="feature.icon"
                size="22"
              />
            </span>
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.text }}</p>
            <router-link
              v-if="feature.docsPath"
              :to="`/documentation/${feature.docsPath}`"
              class="landing-features__docs-link"
            >
              Documentation
            </router-link>
          </li>
        </ul>
      </section>
    </main>

    <footer class="landing-footer">
      <div class="landing-footer__inner">
        <div class="landing-footer__left">
          <p>&copy; {{ year }} AGVA. All rights reserved.</p>
          <router-link
            to="/contact"
            class="landing-footer__link"
          >
            Contact us
          </router-link>
          <router-link
            to="/documentation"
            class="landing-footer__link"
          >
            Documentation
          </router-link>
        </div>
        <div class="landing-footer__meta">
          <div class="landing-footer__status-row">
            <span
              class="landing-footer__dot"
              aria-hidden="true"
            />
            <span class="landing-footer__status">All systems operational</span>
          </div>
          <span class="landing-footer__maintenance">
            <strong>Scheduled maintenance:</strong> {{'not planned'}}
          </span>
          <div class="landing-footer__version-row">
            <span class="landing-footer__version">Version: {{ appVersion }}</span>
            <router-link
              to="/system-status"
              class="landing-footer__link"
            >
              View more
            </router-link>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import {
  BNavbarNav,
  BNavItemDropdown,
  BDropdownItem,
  BDropdownDivider,
  BAvatar,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import { $themeConfig } from '@themeConfig'
import { LANDING_HERO_SLIDES, LANDING_HERO_FIRST_SLIDE_INTERVAL_MS, LANDING_HERO_SLIDE_INTERVAL_MS, LANDING_HERO_TITLE_MAX_CHARS_PER_LINE } from '@/constants/enums'
import { normalizeImageUrl } from '@/libs/utils'
import { initialAbility } from '@/libs/acl/config'

export default {
  name: 'Home',
  components: {
    BNavbarNav,
    BNavItemDropdown,
    BDropdownItem,
    BDropdownDivider,
    BAvatar,
  },
  data() {
    const { companyLogoImage } = $themeConfig.app
    return {
      companyLogoImage,
      year: new Date().getFullYear(),
      appVersion: '1.0.1.18',
      activeSlide: 0,
      heroTimer: null,
      slides: LANDING_HERO_SLIDES.map((slide) => ({
        ...slide,
        loadedImage: null,
      })),
      features: [
        {
          icon: 'UsersIcon',
          title: 'Customer records',
          text: 'Keep contacts, history, and notes together so follow-ups stay fast and personal.',
          docsPath: 'customer-records',
        },
        {
          icon: 'ShoppingCartIcon',
          title: 'Sales & orders',
          text: 'Track deals and orders from quote to payment with a clear status at every step.',
          docsPath: 'orders',
        },
        {
          icon: 'PackageIcon',
          title: 'Inventory & stock',
          text: 'Receive products, watch locations, and catch low stock before it slows the floor.',
          docsPath: 'inventory',
        },
        {
          icon: 'ToolIcon',
          title: 'Services',
          text: 'Define the services you offer, set prices and categories, and add them to orders alongside products.',
          docsPath: 'services',
        },
        {
          icon: 'ShieldIcon',
          title: 'Team permissions',
          text: 'Give supervisors and staff the right access without exposing the whole workspace.',
          docsPath: 'team-permissions',
        },
        {
          icon: 'BarChart2Icon',
          title: 'Dashboards',
          text: 'See sales, returns, and volume at a glance when you need a quick pulse check.',
          docsPath: 'dashboards',
        },
        {
          icon: 'ClockIcon',
          title: 'Stock history',
          text: 'Know who changed stock, which location it affected, and when it happened.',
          docsPath: 'stock-history',
        },
      ],
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/user',
      isAuthenticated: 'auth/authenticated',
      isAuthReady: 'auth/bootstrapped',
    }),
    userPicture() {
      return normalizeImageUrl(this.user && this.user.picture)
    },
    userDisplayName() {
      if (!this.user) {
        return ''
      }
      return this.user.name || this.user.email || ''
    },
    userDisplayRole() {
      if (!this.user || this.user.role === 'Customer') {
        return ''
      }
      return this.user.role || ''
    },
  },
  mounted() {
    this.startHeroSlideshow()
    this.deferHeroImageLoading()
  },
  beforeUnmount() {
    this.stopHeroSlideshow()
  },
  methods: {
    ...mapActions({
      logout: 'auth/logout',
    }),
    async userLogout() {
      await this.logout()
      if (this.$ability) {
        this.$ability.update(initialAbility)
      }
      await this.$router.replace({ name: 'login' })
    },
    deferHeroImageLoading() {
      // Start immediately after paint — do not wait on idle/auth.
      this.$nextTick(() => {
        this.loadHeroImages()
      })
    },
    async loadHeroImages() {
      // Active slide first, then the rest — page stays interactive on gray placeholders.
      const order = [
        this.activeSlide,
        ...this.slides.map((_, index) => index).filter((index) => index !== this.activeSlide),
      ]

      for (let i = 0; i < order.length; i += 1) {
        const index = order[i]
        const slide = this.slides[index]
        if (!slide || !slide.image || slide.loadedImage) {
          continue
        }

        // eslint-disable-next-line no-await-in-loop
        const src = await this.resolveHeroImageUrl(slide.image)
        if (!src) {
          continue
        }

        // eslint-disable-next-line no-await-in-loop
        await this.preloadHeroImage(src)
        slide.loadedImage = src
      }
    },
    resolveHeroImageUrl(filename) {
      // Separate async chunk per image so the initial page never waits on hero assets.
      return import(
        /* webpackChunkName: "landing-hero-[request]" */
        `@/assets/images/landing/${filename}`
      )
        .then((mod) => mod.default || mod)
        .catch(() => null)
    },
    preloadHeroImage(src) {
      return new Promise((resolve) => {
        const img = new Image()
        img.decoding = 'async'
        img.onload = () => resolve()
        img.onerror = () => resolve()
        img.src = src
      })
    },
    heroSlideStyle(slide) {
      if (!slide.loadedImage) {
        return null
      }
      return { backgroundImage: `url(${slide.loadedImage})` }
    },
    startHeroSlideshow() {
      this.stopHeroSlideshow()
      if (!this.slides.length) {
        return
      }
      this.scheduleNextHeroSlide()
    },
    findNextLoadedSlideIndex(fromIndex) {
      const total = this.slides.length
      if (!total) {
        return -1
      }

      for (let offset = 1; offset < total; offset += 1) {
        const index = (fromIndex + offset) % total
        if (this.slides[index] && this.slides[index].loadedImage) {
          return index
        }
      }

      return -1
    },
    scheduleNextHeroSlide() {
      const delay = this.activeSlide === 0
        ? LANDING_HERO_FIRST_SLIDE_INTERVAL_MS
        : LANDING_HERO_SLIDE_INTERVAL_MS
      this.heroTimer = setTimeout(() => {
        this.advanceHeroSlide()
      }, delay)
    },
    advanceHeroSlide() {
      const nextIndex = this.findNextLoadedSlideIndex(this.activeSlide)
      if (nextIndex === -1) {
        // Next slides not ready yet — stay put and check again shortly.
        this.heroTimer = setTimeout(() => {
          this.advanceHeroSlide()
        }, 500)
        return
      }

      this.activeSlide = nextIndex
      this.scheduleNextHeroSlide()
    },
    stopHeroSlideshow() {
      if (this.heroTimer) {
        clearTimeout(this.heroTimer)
        this.heroTimer = null
      }
    },
    goToSlide(index) {
      if (!this.slides[index] || !this.slides[index].loadedImage) {
        return
      }
      this.activeSlide = index
      this.startHeroSlideshow()
    },
    heroTitleLines(title) {
      const words = String(title || '').trim().split(/\s+/).filter(Boolean)
      if (!words.length) {
        return ['']
      }

      const maxChars = LANDING_HERO_TITLE_MAX_CHARS_PER_LINE
      const lines = []
      let current = ''

      words.forEach((word) => {
        const next = current ? `${current} ${word}` : word
        if (next.length <= maxChars) {
          current = next
          return
        }
        if (current) {
          lines.push(current)
        }
        current = word
      })

      if (current) {
        lines.push(current)
      }

      return lines
    },
  },
}
</script>

<style scoped>
.landing {
  --landing-accent: #4f439e;
  --landing-accent-deep: #3b3278;
  --landing-accent-soft: #ebe7f8;
  --landing-ink: #1c1833;
  --landing-muted: #5c5678;
  --landing-line: rgba(79, 67, 158, 0.22);
  --landing-surface: #f4f2fb;
  --landing-white: #ffffff;
  --landing-display: Arial, Helvetica, sans-serif;
  --landing-body: Arial, Helvetica, sans-serif;

  min-height: 100vh;
  color: var(--landing-ink);
  font-family: var(--landing-body);
  background:
    radial-gradient(1200px 600px at 12% -10%, rgba(79, 67, 158, 0.18), transparent 60%),
    radial-gradient(900px 500px at 100% 0%, rgba(79, 67, 158, 0.12), transparent 55%),
    linear-gradient(180deg, #faf9ff 0%, var(--landing-surface) 40%, #eeeaf8 100%);
}

.landing-nav {
  position: sticky;
  top: 0;
  z-index: 50;
  backdrop-filter: blur(12px);
  background: rgba(250, 249, 255, 0.86);
  border-bottom: 1px solid var(--landing-line);
}

.landing-nav__inner {
  width: 100%;
  margin: 0;
  padding: 0.75rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.landing-nav__brand {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-right: auto;
}

.landing-nav__logo {
  height: 36px;
  width: auto;
  display: block;
}

.landing-nav__menu {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-left: auto;
}

.landing-nav__register {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 1.1rem;
  border-radius: 0.55rem;
  background: transparent;
  color: var(--landing-accent) !important;
  border: 1px solid var(--landing-line);
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.landing-nav__register:hover {
  background: var(--landing-accent-soft);
  color: var(--landing-accent-deep) !important;
  transform: translateY(-1px);
}

.landing-nav__login {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 1.1rem;
  border-radius: 0.55rem;
  background: var(--landing-accent);
  color: #fff !important;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: background 0.2s ease, transform 0.2s ease;
}

.landing-nav__login:hover {
  background: var(--landing-accent-deep);
  color: #fff !important;
  transform: translateY(-1px);
}

.landing-nav__authenticated {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.landing-nav__admin {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 1.1rem;
  border-radius: 0.55rem;
  background: transparent;
  color: var(--landing-accent) !important;
  border: 1px solid var(--landing-line);
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.landing-nav__admin:hover {
  background: var(--landing-accent-soft);
  color: var(--landing-accent-deep) !important;
  transform: translateY(-1px);
}

.landing-nav__user {
  margin: 0;
  padding: 0;
}

.landing-nav__user :deep(.nav-link) {
  padding: 0;
}

.landing-nav__user-toggle {
  border: 0;
  background: transparent;
  box-shadow: none;
}

.landing-nav__user :deep(.user-name) {
  color: var(--landing-ink);
  font-size: 0.95rem;
}

.landing-nav__user :deep(.user-status) {
  color: var(--landing-muted);
  font-size: 0.82rem;
}

.landing-hero {
  position: relative;
  width: 100%;
  aspect-ratio: 1920 / 1105;
  height: auto;
  overflow: hidden;
  background: #d8d8d8;
}

.landing-hero__slide {
  position: absolute;
  inset: 0;
  background-color: #d8d8d8;
  background-size: cover;
  background-position: left top;
  background-repeat: no-repeat;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.55s ease;
}

.landing-hero__slide::before {
  content: '';
  position: absolute;
  inset: 0;
  background-color: #d8d8d8;
  transition: opacity 0.4s ease;
  pointer-events: none;
}

.landing-hero__slide--loaded::before {
  opacity: 0;
}

.landing-hero__slide::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(28, 24, 51, 0.08) 40%, rgba(28, 24, 51, 0.42) 100%);
  pointer-events: none;
}

.landing-hero__slide--active {
  opacity: 1;
}

.landing-hero__caption {
  position: relative;
  z-index: 1;
  display: flex;
  height: 100%;
  padding: 6.75rem 2.25rem 4.5rem;
}

.landing-hero__caption--top-left {
  align-items: flex-start;
  justify-content: flex-start;
  padding-left: 4.5rem;
}

.landing-hero__caption--top-right {
  align-items: flex-start;
  justify-content: flex-end;
  padding-right: 4.5rem;
}

.landing-hero__caption--bottom-left {
  align-items: flex-end;
  justify-content: flex-start;
  padding-left: 4.5rem;
}

.landing-hero__caption--bottom-right {
  align-items: flex-end;
  justify-content: flex-end;
  padding-right: 4.5rem;
}

.landing-hero__caption--center {
  align-items: center;
  justify-content: center;
}

.landing-hero__caption--align-left {
  text-align: left;
}

.landing-hero__caption--align-right {
  text-align: right;
}

.landing-hero__caption--align-center {
  text-align: center;
}

.landing-hero__caption--align-left .landing-hero__title {
  align-items: flex-start;
}

.landing-hero__caption--align-right .landing-hero__title {
  align-items: flex-end;
}

.landing-hero__caption--align-center .landing-hero__title {
  align-items: center;
}

.landing-hero__title {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.28em;
  font-family: Arial, Helvetica, sans-serif;
  font-size: clamp(1.55rem, 4.2vw, 3.15rem);
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.15;
  text-align: inherit;
}

.landing-hero__line {
  display: inline-block;
  width: fit-content;
  max-width: 100%;
  padding: 0.12em 0.45em;
}

.landing-hero__dots {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 1.35rem;
  z-index: 2;
  display: flex;
  justify-content: center;
  gap: 0.6rem;
}

.landing-hero__dot {
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  position: relative;
}

.landing-hero__dot::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.45);
  transform: translate(-50%, -50%);
  transition: background 0.2s ease, transform 0.2s ease, width 0.2s ease, height 0.2s ease;
}

.landing-hero__dot:hover::after {
  background: rgba(255, 255, 255, 0.8);
}

.landing-hero__dot.is-active::after {
  background: #fff;
  width: 14px;
  height: 14px;
}

.landing-hero-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.85rem;
  padding: 1.75rem 1.25rem 0.5rem;
}

.landing-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.7rem 1.25rem;
  border-radius: 0.55rem;
  font-weight: 600;
  font-size: 0.98rem;
  text-decoration: none;
  border: 1px solid transparent;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.landing-btn--primary {
  background: var(--landing-accent);
  color: #fff !important;
}

.landing-btn--primary:hover {
  background: var(--landing-accent-deep);
  color: #fff !important;
  transform: translateY(-1px);
}

.landing-btn--ghost {
  background: transparent;
  color: var(--landing-accent) !important;
  border-color: var(--landing-line);
}

.landing-btn--ghost:hover {
  background: var(--landing-accent-soft);
  color: var(--landing-accent-deep) !important;
}

.landing-btn--lg {
  padding: 0.85rem 1.5rem;
  font-size: 1.05rem;
}

.landing-btn--sm {
  padding: 0.4rem 0.85rem;
  font-size: 0.85rem;
  border-radius: 0.45rem;
}

.landing-section {
  max-width: 1120px;
  margin: 0 auto;
  padding: 4rem 1.25rem;
}

.landing-section--alt {
  border-top: 1px solid var(--landing-line);
  border-bottom: 1px solid var(--landing-line);
  background: rgba(255, 255, 255, 0.35);
}

.landing-section__title {
  margin: 0 0 0.75rem;
  font-family: var(--landing-display);
  font-size: clamp(1.5rem, 2.8vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.03em;
}

.landing-section__lead {
  margin: 0 0 2rem;
  max-width: 36rem;
  color: var(--landing-muted);
  line-height: 1.55;
}

.landing-features {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.75rem 1.5rem;
}

.landing-features__item h3 {
  margin: 0.35rem 0 0.4rem;
  font-family: var(--landing-display);
  font-size: 1.1rem;
  font-weight: 700;
}

.landing-features__item p {
  margin: 0;
  color: var(--landing-muted);
  line-height: 1.5;
  font-size: 0.95rem;
}

.landing-features__docs-link {
  display: inline-block;
  margin-top: 0.65rem;
  color: var(--landing-accent);
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: underline;
}

.landing-features__docs-link:hover {
  color: var(--landing-accent-deep);
}

.landing-features__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--landing-accent);
  line-height: 1;
}

.landing-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: #1c1833;
  color: rgba(255, 255, 255, 0.78);
}

.landing-footer__inner {
  width: 100%;
  margin: 0;
  padding: 1.5rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  color: inherit;
  font-size: 0.9rem;
}

.landing-footer__left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.landing-footer p {
  margin: 0;
  color: rgba(255, 255, 255, 0.72);
}

.landing-footer__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.55rem 0.85rem;
  margin-left: auto;
}

.landing-footer__status-row,
.landing-footer__version-row {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.landing-footer__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #3dd68c;
  box-shadow: 0 0 0 3px rgba(61, 214, 140, 0.22);
  flex-shrink: 0;
}

.landing-footer__status {
  color: #ffffff;
  font-weight: 600;
}

.landing-footer__maintenance {
  color: rgba(255, 255, 255, 0.68);
  font-weight: 500;
}

.landing-footer__maintenance strong {
  color: rgba(255, 255, 255, 0.9);
}

.landing-footer__version {
  color: rgba(255, 255, 255, 0.68);
  font-variant-numeric: tabular-nums;
}

.landing-footer__link {
  color: #c9c2ef;
  font-weight: 600;
  text-decoration: underline;
}

.landing-footer__link:hover {
  color: #ffffff;
}

@media (max-width: 991.98px) {
  .landing-hero__caption {
    padding: 3.5rem 1.25rem 3.25rem;
  }

  .landing-hero__caption--top-left,
  .landing-hero__caption--bottom-left {
    padding-left: 1.25rem;
  }

  .landing-hero__caption--top-right,
  .landing-hero__caption--bottom-right {
    padding-right: 1.25rem;
  }

  .landing-hero__title {
    font-size: clamp(0.525rem, 1.6vw, 0.875rem);
  }

  .landing-features {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 767.98px) {
  .landing-nav__register,
  .landing-nav__login {
    padding: 0.45rem 0.85rem;
    font-size: 0.88rem;
  }

  .landing-hero__caption {
    padding: 2.25rem 0.85rem 2.75rem;
  }

  .landing-hero__caption--top-left,
  .landing-hero__caption--bottom-left {
    padding-left: 0.85rem;
  }

  .landing-hero__caption--top-right,
  .landing-hero__caption--bottom-right {
    padding-right: 0.85rem;
  }

  .landing-hero__title {
    font-size: clamp(0.88rem, 3.4vw, 1.15rem);
    gap: 0.2em;
  }

  .landing-hero__line {
    padding: 0.1em 0.32em;
  }

  .landing-features {
    grid-template-columns: 1fr;
  }

  .landing-footer__inner {
    flex-direction: column;
    align-items: stretch;
    gap: 0.85rem;
  }

  .landing-footer__left {
    width: 100%;
    justify-content: space-between;
    flex-wrap: nowrap;
  }

  .landing-footer__meta {
    width: 100%;
    margin-left: 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.45rem;
  }

  .landing-footer__status-row,
  .landing-footer__version-row {
    width: 100%;
  }
}
</style>
